import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import { PlayArrow as PlayIcon } from '@mui/icons-material';
import { NotificationService } from '../../../services/notificationService';
import { NotificationType, NotificationPriority } from '../../../types/user';

interface DeliveryTest {
  userId: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  success: boolean;
  notificationId?: string;
  error?: string;
}

/**
 * DeliveryTester - Test notification delivery performance
 */
const DeliveryTester: React.FC = () => {
  const [userId, setUserId] = useState('');
  const [testing, setTesting] = useState(false);
  const [results, setResults] = useState<DeliveryTest[]>([]);

  const runDeliveryTest = async () => {
    if (!userId.trim()) {
      alert('Please enter a user ID');
      return;
    }

    setTesting(true);
    const testResults: DeliveryTest[] = [];

    // Run 5 delivery tests
    for (let i = 0; i < 5; i++) {
      const startTime = Date.now();
      const test: DeliveryTest = {
        userId: userId.trim(),
        startTime,
        success: false,
      };

      try {
        const notification = await NotificationService.createNotification({
          userId: userId.trim(),
          type: 'custom' as NotificationType,
          title: `Delivery Test ${i + 1}`,
          message: 'Testing notification delivery speed',
          priority: NotificationPriority.NORMAL,
          data: { deliveryTest: true, testNumber: i + 1 },
        });

        test.endTime = Date.now();
        test.duration = test.endTime - startTime;
        test.success = true;
        test.notificationId = notification.id;
      } catch (error: any) {
        test.endTime = Date.now();
        test.duration = test.endTime - startTime;
        test.success = false;
        test.error = error.message;
      }

      testResults.push(test);
      setResults([...testResults]);

      // Small delay between tests
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    setTesting(false);
  };

  const avgDuration = results.length > 0
    ? results.reduce((sum, r) => sum + (r.duration || 0), 0) / results.length
    : 0;

  const successRate = results.length > 0
    ? (results.filter(r => r.success).length / results.length) * 100
    : 0;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Delivery Performance Tester
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Test notification delivery speed and reliability
      </Typography>

      <Box display="flex" gap={2} mb={3}>
        <TextField
          fullWidth
          label="Test User ID"
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          disabled={testing}
          placeholder="Enter a user ID to test"
        />
        <Button
          variant="contained"
          startIcon={testing ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />}
          onClick={runDeliveryTest}
          disabled={testing || !userId.trim()}
          sx={{ minWidth: 150 }}
        >
          {testing ? 'Testing...' : 'Run Test'}
        </Button>
      </Box>

      {results.length > 0 && (
        <>
          <Box display="flex" gap={2} mb={2}>
            <Chip
              label={`Success Rate: ${successRate.toFixed(0)}%`}
              color={successRate === 100 ? 'success' : 'warning'}
            />
            <Chip
              label={`Avg. Duration: ${avgDuration.toFixed(0)}ms`}
              color="info"
            />
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Test #</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Notification ID</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((result, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{result.duration}ms</TableCell>
                    <TableCell>
                      <Chip
                        label={result.success ? 'Success' : 'Failed'}
                        color={result.success ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {result.notificationId ? (
                        <Typography variant="caption">{result.notificationId.substring(0, 8)}...</Typography>
                      ) : (
                        <Typography variant="caption" color="error">{result.error}</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {!testing && (
            <Alert severity={successRate === 100 ? 'success' : 'warning'} sx={{ mt: 2 }}>
              {successRate === 100
                ? `All tests passed! Average delivery time: ${avgDuration.toFixed(0)}ms`
                : `${results.filter(r => !r.success).length} test(s) failed. Check system health.`}
            </Alert>
          )}
        </>
      )}
    </Paper>
  );
};

export default DeliveryTester;
